import React, { Component, ComponentType } from 'react'
import { Box, BoxProps, Stack, StackProps, styled } from '@mui/material'
import { SortButton } from './ui.units'
import { createViewMode } from './create.view-mode'
import { defaultViewMode, ViewModeButtons } from './view-mode.units'
import { getViewModeFromURL, syncViewModeToURL } from './query-param-url'
import { EViewMode, IViewModeConfigs, IViewModeValidModel } from './view-mode.types'
import { IDataMode, IFilterBarConfigs, IFilterState, IFilterStateChangeHandler, IPositionElement, IViewData } from './types'
import { defaultFilterBarState, DataViewContext, IDataViewState, IDataViewContext, OnPanelValueChange } from './context'
import createFilterBar, { IFilterBarSlots } from './create.filter-bar'
import ScrollTracking, { IScrollTrackingData, IScrollTrackingOptions } from './scroll-tracking'
import createPaginationBar, { IPaginationBarConfigs } from './create.pagination-bar'
import createPopperPanel from './create.popper-panel'
import createActiveFiltersPanel, { IActiveFiltersPanelProps } from './create.active-filters-panel'
import { dataViewClasses, DataViewStyled } from './styleds'

export const filterBarClasses = {
  root: 'FilterBar-root',
  content: 'FilterBar-content',
  input: 'FilterBar-input',
  inputWrap: 'FilterBar-inputWrap',
  label: 'FilterBar-label',
  button: 'FilterBar-button',
  spacing: 'FilterBar-spacing',
  panel: 'FilterBar-panel',
  sticky: 'FilterBar-sticky',
  shadow: 'FilterBar-shadow'
}

export interface IDataViewSlots<T extends IViewModeValidModel> {
  filterWrapProps?: BoxProps
  filterSlots?: IPositionElement
  activeFiltersPanelSlots?: IPositionElement
  activeFiltersPanelProps?: Omit<IActiveFiltersPanelProps, 'loading'>
  primaryInputSlots?: IFilterBarSlots
  filterBarConfigs?: IFilterBarConfigs<T>
  viewModeConfigs?: IViewModeConfigs<T>
  paginationBarConfigs?: IPaginationBarConfigs
  scrollTracking?: IScrollTrackingOptions
  wrapProps?: StackProps
  footerProps?: {
    wrapProps?: BoxProps
    enablePagination?: boolean
  }
  title?: React.ReactNode
  mode?: IDataMode
  clientFilter?: IClientFilterHandler<T>
}

export interface IClientFilterParams<T extends IViewModeValidModel> {
  item: T
  index: number
  items: T[]
  filterState: IFilterState<T>
  keyword: string
}

export type IClientFilterHandler<T extends IViewModeValidModel> = (params: IClientFilterParams<T>) => boolean

export interface IDataViewProps<T extends IViewModeValidModel> {
  loading?: boolean
  error?: string
  data?: IViewData<T>
  filter?: IFilterState<T>
  onFilterChange?: (value: IFilterState<T>) => void
  viewMode?: EViewMode
  onViewModeChange?: (value: EViewMode) => void
  syncViewModeToURL?: boolean
  autoHeight?: boolean
  slots?: IDataViewSlots<T>
}

export interface IDataViewConfigs<T extends IViewModeValidModel> {
  autoHeight?: boolean
  mode?: IDataMode
  clientFilter?: IClientFilterHandler<T>
  filterBar?: IFilterBarConfigs<T>
  viewMode?: IViewModeConfigs<T>
  paginationBar?: IPaginationBarConfigs
  scrollTracking?: IScrollTrackingOptions
}

interface IInternalConfigs<T extends IViewModeValidModel> extends IDataViewConfigs<T> {
  enablePaginationTop?: boolean
  scrollTracking?: IScrollTrackingOptions
}

function createDataView<T extends IViewModeValidModel>(configs: IDataViewConfigs<T>): ComponentType<IDataViewProps<T>> {
  const FilterBarInstance = createFilterBar<T>()
  const PopperPanelInstance = createPopperPanel<T>()
  const ActiveFiltersPanelInstance = createActiveFiltersPanel<T>()
  const ViewModeInstance = createViewMode<T>(configs.viewMode)
  const PaginationBarInstance = createPaginationBar<T>()

  class DataView extends Component<IDataViewProps<T>, IDataViewState<T>> {
    constructor(props: IDataViewProps<T>) {
      super(props)
      this.mergeConfig(props)
      this.state = this.getDefaultState(props)
    }

    private getDefaultState(currentProps: IDataViewProps<T>): IDataViewState<T> {
      const defaultValue = currentProps.slots?.viewModeConfigs?.defaultValue ?? configs.viewMode?.defaultValue ?? defaultViewMode
      const viewMode = currentProps.syncViewModeToURL ? getViewModeFromURL<T>({ defaultViewMode: defaultValue }) : defaultValue
      return {
        ...defaultFilterBarState,
        filterState: this.internalConfig.filterBar?.defaultValue ?? {},
        viewMode
      }
    }

    get filterState(): IFilterState<T> {
      return this.props.filter ?? this.state.filterState
    }

    get viewMode(): EViewMode | undefined {
      return this.props.viewMode ?? this.state.viewMode
    }

    private mergeConfig = (currentProps: IDataViewProps<T>) => {
      const { slots } = currentProps
      this.configCache = {
        filterBar: Object.assign({}, configs.filterBar, slots?.filterBarConfigs),
        viewMode: slots?.viewModeConfigs,
        paginationBar: Object.assign({}, configs.paginationBar, slots?.paginationBarConfigs),
        enablePaginationTop: slots?.paginationBarConfigs?.enablePaginationTop ?? configs.paginationBar?.enablePaginationTop ?? false,
        scrollTracking: Object.assign({}, configs.scrollTracking, slots?.scrollTracking),
        autoHeight: currentProps.autoHeight || configs.autoHeight || false,
        mode: slots?.mode ?? configs.mode ?? 'server',
        clientFilter: slots?.clientFilter ?? configs.clientFilter
      }
    }

    private configCache: IInternalConfigs<T> = {}
    get internalConfig() {
      return this.configCache
    }

    shouldComponentUpdate(nextProps: Readonly<IDataViewProps<T>>): boolean {
      this.mergeConfig(nextProps)
      return true
    }

    get defaultPagination() {
      const defaultPagination = this.internalConfig.filterBar?.defaultValue?.pagination
      return {
        page: defaultPagination?.page ?? 0,
        pageSize: defaultPagination?.pageSize ?? this.internalConfig.paginationBar?.rowsPerPageOptions?.[0] ?? 10
      }
    }

    get currentPagination() {
      const pagination = this.filterState?.pagination
      return {
        page: pagination?.page ?? this.defaultPagination.page,
        pageSize: pagination?.pageSize ?? this.defaultPagination.pageSize
      }
    }

    get totalItems() {
      if (this.internalConfig.mode === 'client') {
        return this.filteredClientItems.length
      }
      return this.props.data?.totalItems ?? 0
    }

    get filteredClientItems() {
      const sourceItems = this.props.data?.items ?? []
      if (this.internalConfig.mode !== 'client') return sourceItems

      const clientFilter = this.internalConfig.clientFilter
      if (!clientFilter) return sourceItems

      const filterState = this.filterState
      const keyword = this.state.keyword.trim()

      return sourceItems.filter((item, index, items) => clientFilter({ item, index, items, filterState, keyword }))
    }

    get viewDataItems() {
      const sourceItems = this.filteredClientItems
      if (this.internalConfig.mode !== 'client') return sourceItems

      const { page, pageSize } = this.currentPagination
      if (pageSize <= 0) return sourceItems

      const start = Math.max(page, 0) * pageSize
      return sourceItems.slice(start, start + pageSize)
    }

    refFilterButton: HTMLButtonElement | null = null
    refSortButton: HTMLButtonElement | null = null
    render() {
      const { slots } = this.props

      const mapContext: IDataViewContext<T> = {
        keyword: this.state.keyword.trim(),
        filterBarConfigs: this.internalConfig.filterBar ?? {},
        filterState: this.filterState,
        anchorEl: this.state.anchorEl,
        area: this.state.area,
        viewMode: this.state.viewMode,
        scrolled: this.state.scrolled,
        onFilterStateChange: this.onFilterStateChangeHandler,
        onKeywordChange: this.onKeywordChangeHandler,
        onPopperPanelChange: this.onPopperPanelChangeHandler,
        onPanelClose: this.onPanelCloseHandler,
        onClear: this.onClearHandler
      }

      return (
        <DataViewContext.Provider value={mapContext}>
          <DataViewStyled {...this.props.slots?.wrapProps}>
            {this.props.slots?.title}
            {this.internalConfig.filterBar?.enableSticky && (
              <ScrollTracking onChange={this.onScrollChangeHandler} {...this.internalConfig.scrollTracking} />
            )}
            {this.props.slots?.filterSlots?.above}
            <FilterBarInstance
              id={this.internalConfig.scrollTracking?.elementId}
              slots={{
                ...this.props.slots?.primaryInputSlots,
                right: (
                  <>
                    {this.internalConfig.filterBar?.enableSort && <SortButton />}
                    {this.props.slots?.primaryInputSlots?.right}
                  </>
                )
              }}
            >
              <PopperPanelInstance />
            </FilterBarInstance>
            {this.props.slots?.filterSlots?.below}
            {this.props.slots?.activeFiltersPanelSlots?.above}
            <div className={dataViewClasses.filterPanel}>
              <ViewModeButtons value={this.state.viewMode} onChange={this.onViewModeChangeHandler} />
              {this.props.slots?.activeFiltersPanelSlots?.left}
              <div className={dataViewClasses.filterPannelInner}>
                <ActiveFiltersPanelInstance loading={this.props.loading} {...slots?.activeFiltersPanelProps} />
                <div className={dataViewClasses.filterPannelInnerRight}>
                  {this.internalConfig.enablePaginationTop && (
                    <PaginationBarInstance
                      count={this.totalItems}
                      defaultFilter={this.internalConfig.filterBar?.defaultValue}
                      filter={this.filterState}
                      onChange={this.onFilterStateChangeHandler}
                      {...this.internalConfig.paginationBar}
                    />
                  )}
                </div>
              </div>
              {this.props.slots?.activeFiltersPanelSlots?.right}
            </div>
            {this.props.slots?.activeFiltersPanelSlots?.below}
            <ViewModeInstance
              data={this.viewDataItems}
              viewMode={this.viewMode}
              loading={this.props.loading}
              error={this.props.error}
              autoHeight={this.internalConfig.autoHeight}
              slots={this.internalConfig.viewMode}
            />
            {this.renderFooter()}
          </DataViewStyled>
        </DataViewContext.Provider>
      )
    }

    renderFooter = () => {
      const temp = this.props.slots?.footerProps || {}
      const classes = [dataViewClasses.footerPanel, temp.wrapProps?.className].filter(Boolean)
      return (
        <Box {...temp.wrapProps} className={classes.join(' ')}>
          {temp.enablePagination !== false && (
            <PaginationBarInstance
              count={this.totalItems}
              defaultFilter={this.internalConfig.filterBar?.defaultValue}
              filter={this.filterState}
              onChange={this.onFilterStateChangeHandler}
              {...this.internalConfig.paginationBar}
            />
          )}
        </Box>
      )
    }

    onViewModeChangeHandler = (value: EViewMode) => {
      this.setState({ viewMode: value }, () => {
        if (this.props.syncViewModeToURL) {
          const dViewMode = this.internalConfig.viewMode?.defaultValue ?? configs.viewMode?.defaultValue ?? defaultViewMode
          syncViewModeToURL({ viewMode: value, defaultViewMode: dViewMode })
        }
        this.props.onViewModeChange?.(value)
      })
    }

    onKeywordChangeHandler = (keyword: string) => {
      const finalVal = keyword.trim()
      if (finalVal === this.state.keyword) return
      this.setState({ keyword: finalVal })
    }

    onScrollChangeHandler = (data: IScrollTrackingData) => this.setState({ scrolled: data.isScrolled })

    onPopperPanelChangeHandler: OnPanelValueChange = (anchorEl, area) => this.setState({ anchorEl, area })

    onClearHandler = () => this.setState(defaultFilterBarState)

    onPanelCloseHandler = () => {
      this.setState({ anchorEl: null, area: undefined, keyword: '' })
    }

    onFilterStateChangeHandler: IFilterStateChangeHandler<T> = (filterState: IFilterState<T>) => {
      if (!this.props.filter) this.setState({ ...defaultFilterBarState, filterState })
      this.props.onFilterChange && this.props.onFilterChange(filterState)
      if (!!this.props.filter) this.setState(defaultFilterBarState)
    }
  }

  return DataView
}

export default createDataView
